"use client";
import { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { blogPosts } from "../utils/blogPosts";

const OtherBlogs = ({ currentBlogId, type: propType }) => {
  const { type: paramType } = useParams(); // ✅ get current product type from URL
  const type = propType || paramType;
  const [neighbors, setNeighbors] = useState([]);

  useEffect(() => {
    if (!currentBlogId || !type || !Array.isArray(blogPosts)) return;

    // ✅ filter only blogs with same product type
    const sameTypeBlogs = blogPosts.filter((b) => b.type === type);

    const index = sameTypeBlogs.findIndex(
      (b) => String(b.blogId) === String(currentBlogId)
    );
    if (index === -1) return;

    // helper to wrap around within the same type list
    const getAt = (i) => sameTypeBlogs[(i + sameTypeBlogs.length) % sameTypeBlogs.length];

    // pick 1 previous + 2 next
    const prev = getAt(index - 1);
    const next1 = getAt(index + 1);
    const next2 = getAt(index + 2);

    setNeighbors([prev, next1, next2]);
  }, [currentBlogId, type]);

  return (
    <BlogContainer>
      <BlogHeader type={type} />
      <BlogGrid>
        {neighbors.map((post, index) => (
          <BlogCard
            key={index}
            blogId={post.blogId}
            image={post.imageUrl}
            date={post.date}
            title={post.title}
            type={post.type}
            objectPosition={post.objectPosition}
          />
        ))}
      </BlogGrid>
    </BlogContainer>
  );
};

//
// ===== Header =====
//
const BlogHeader = ({ type }) => {
  const formatType = (slug) => {
    const map = {
      "ai-lms": "LXP",
      "ai-lms-students": "LXP Students",
      "smart-forms": "Smart Forms",
      "ai-assistant": "AI Assistant",
    };
    return map[slug] || "Campus Insights";
  };

  return (
    <HeaderContainer>
      <TitleWrapper>
        <Title>More from {formatType(type)}</Title>
      </TitleWrapper>
    </HeaderContainer>
  );
};

//
// ===== Card =====
//
const BlogCard = ({ image, date, title, blogId, type, objectPosition }) => {
  const navigate = useNavigate();

  const handleLearnMore = () => {
    window.scrollTo(0, 0);
    navigate(`/blog/${type}/${blogId}`);
  };

  return (
    <CardContainer>
      <CardImage src={image} alt={title} objectPosition={objectPosition} />
      <CardContent>
        <PublishDate>{date}</PublishDate>
        <ContentWrapper>
          <ArticleTitle>{title}</ArticleTitle>
          <LearnMoreButton onClick={handleLearnMore}>
            <ButtonContent>
              <ButtonText>Learn More</ButtonText>
              <ArrowIcon />
            </ButtonContent>
          </LearnMoreButton>
        </ContentWrapper>
      </CardContent>
    </CardContainer>
  );
};

const ArrowIcon = () => {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '24px', height: '24px', flexShrink: 0 }}
        >
            <g clipPath="url(#clip0_arrow)">
                <path
                    d="M15 5L13.59 6.41L18.17 11H2V13H18.17L13.58 17.59L15 19L22 12L15 5Z"
                    fill="#6A37E3"
                />
            </g>
            <defs>
                <clipPath id="clip0_arrow">
                    <rect width="24" height="24" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};

const CardContainer = styled.article`
  border-radius: 16px;
  background-color: #fff;
  width: 340px;
  height: 420px; /* fixed height for uniform cards */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
`;

const CardImage = styled.img`
  width: 100%;
  height: 180px; /* fixed image height */
  object-fit: cover;
  border-radius: 16px 16px 0 0;
  object-position: ${({ objectPosition }) => objectPosition || "center"};
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
`;

const PublishDate = styled.time`
  color: #1c1b1f;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const ContentWrapper = styled.div`
  display: flex;
  margin-top: 16px;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  font-weight: 600;
  justify-content: start;
`;

const ArticleTitle = styled.h2`
  color: #1c1b1f;
  font-size: 18px;
  line-height: 25px;
  letter-spacing: -0.18px;
`;

const LearnMoreButton = styled.button`
  border-radius: 100px;
  align-self: start;
  display: flex;
  margin: 20px 0 13px;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #6a37e3;
  letter-spacing: -0.07px;
  line-height: 1.4;
  justify-content: center;
  padding: 10px 16px;
  border: 1px solid rgba(106, 55, 227, 1);
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(106, 55, 227, 0.1);
  }
`;

const ButtonContent = styled.div`
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: start;
  margin: auto 0;
`;

const ButtonText = styled.span`
  align-self: stretch;
  margin: auto 0;
`;

const HeaderContainer = styled.header`
  align-self: center;
  display: flex;
  width: 636px;
  max-width: 100%;
  flex-direction: column;
  align-items: center;
  font-size: 48px;
  color: #1c1b1f;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.48px;
  line-height: 1.2;
  justify-content: start;
  @media (max-width: 991px) {
    font-size: 40px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  @media (max-width: 991px) {
    font-size: 40px;
  }
`;

const Title = styled.div`
  align-self: stretch;
  margin-top: 40px;
  font: 700 40px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    margin-top: 30px;
    font-size: 24px;

  }
`;

const BlogContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-family: Inter, sans-serif;
  justify-content: start;
  width: 1120px;
  max-width: 100%;
  margin: 60px auto 0;
  padding: 0 16px;
  box-sizing: border-box;
`;


const BlogGrid = styled.div`
  display: flex;
  margin-top: 60px;
  width: 100%;
  align-items: center;
  gap: 30px;
  justify-content: start;
  flex-wrap: wrap;
  @media (max-width: 991px) {
    max-width: 100%;
    margin-top: 40px;
  }
`;

export default OtherBlogs;
